import { DecisionTreeJob, DecisionTreeStart } from "../../shared/tree";

/**
 * How asking for the decision trees of a case ended: with the trees, or with
 * the reason there are none. `busy`: the server generates one case at a time,
 * and it was another user's turn.
 */
export type DecisionTreeResult =
  Exclude<DecisionTreeJob, { state: "running" }> | { state: "busy" };

// The server drops a job nobody has asked for in 15 seconds
// (DecisionTreeJobOptions.abandonedAfterMs), so this has to stay well below.
const ASK_EVERY_MS = 500;

const sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

/**
 * Has the server generate the decision trees of a case and asks for them until
 * the generation has ended. The server works on them beside its other
 * requests and answers each question with how far it is.
 *
 * Aborting stops the asking; the server then ends the job on its own.
 *
 * @param run calls a cloud function
 * @param onProgress told the share of the work done, 0 to 1
 */
export async function fetchDecisionTrees(
  run: (name: string, params: object) => Promise<unknown>,
  caseId: string,
  onProgress: (share: number) => void,
  signal: AbortSignal,
  pause: (ms: number) => Promise<void> = sleep,
): Promise<DecisionTreeResult> {
  const started = (await run("startDecisionTrees", {
    caseId,
  })) as DecisionTreeStart;
  if ("busy" in started) return { state: "busy" };

  for (;;) {
    signal.throwIfAborted();
    const job = (await run("getDecisionTrees", {
      jobId: started.jobId,
    })) as DecisionTreeJob;
    if (job.state !== "running") return job;
    onProgress(job.progress);
    await pause(ASK_EVERY_MS);
  }
}
